/**
 * The Fleet screen feature (M6 plan P28, spec §8; the career in the Reach only, with the `fleet` service from P21's
 * fleet link). The Fleet screen is the dashboard's **Fleet › Ships** section (dashboard spec §2.2, W3 card M2;
 * `registerDashSection`); U (bindings `fleetScreen`) is its deep link, served by the dashboard feature
 * (`dashboard.json deepLinks`, on since W3 card M4). A row's open goes to the ship detail hook
 * (`setShipOpener`); P29 sets it to the ship detail and standing-order editor (`shipDetailHost.tsx`), a layer above
 * the screen that takes the keys and the pad while open (needs the `strategic` client).
 *
 * `frame` allocates nothing except on the open and close events (D-014).
 */
import { FEATURE_ORDER, type FrameInfo, type GameContext, type GameFeature } from '../../app/feature'
import { resolveModule } from '../../data/m2'
import { isModuleId, type M2Data } from '../../data/m2Schemas'
import { systemId as placementSystemId } from '../../galaxy/placement'
import { createPadNav, stepPadNav, type PadNavState } from '../../hud/menu'
import { loadSettingsConfig } from '../../settings/settings'
import { ownedShip } from '../../fleet-link/hangar'
import type { ReachRuntime } from '../../reach/runtime/types'
import { parseStationId } from '../../strategic/ids'
import type { FleetShipView } from '../../strategic/types'
import { RAW_WARE_IDS, WARE_IDS } from '../../strategic/wares'
import { openDashboard, registerDashSection } from '../../ui/dashboard/registry'
import { createFleetSection, type FleetSectionHost } from '../../ui/fleet/fleetHost'
import { fleetSection, FLEET_SECTION_IDS } from '../../ui/fleet/sectionHost'
import { loadFleetScreenConfig, loadoutLines, loadPresets, shipLocation, type NameLookup } from '../../ui/fleet/fleetModel'
import { loadOrderEditorData, type Choice, type EditorOptions } from '../../ui/fleet/orderModel'
import type { LoadoutLine } from '../../ui/fleet/ShipDetail'
import { createShipDetailOverlay, type ShipDetailOverlay } from '../../ui/fleet/shipDetailHost'
import { wareName } from '../../ui/strategic/names'

/** After the Wire (525) and the Charter Board (526), before the Charter HUD (530). */
const ORDER = FEATURE_ORDER.wire + 2

export interface FleetScreenFeature extends GameFeature {
  readonly screen: FleetSectionHost | null
  /** Opens the dashboard at Fleet › Ships (the debug API). */
  openFleet(): void
  /** The ship detail hook (P29): called with a ship id when a row is opened; null clears it. */
  setShipOpener(fn: ((shipId: string) => void) | null): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

/**
 * Display names from the Reach (D-459 bug 4): a station resolves in any system, through the layout of the system its
 * id names (`sys-07-st2`) and of the ship's system; a station no layout has (built in play) reads
 * "<system> station", never its id. The Fleet screen prefers the core's own name (`FleetShipView.stationName`).
 */
export function reachNames(reach: Pick<ReachRuntime, 'galaxy' | 'layoutOf'> | null): NameLookup {
  const systems = new Map((reach?.galaxy.systems ?? []).map((s) => [s.id, s.name]))
  const stations = new Map<string, string>()
  const loaded = new Set<string>()
  function load(sys: string | null): void {
    if (reach === null || sys === null || loaded.has(sys) || !systems.has(sys)) return
    loaded.add(sys)
    for (const st of reach.layoutOf(sys).stations) stations.set(st.id, st.name)
  }
  return {
    system: (id) => systems.get(id) ?? id,
    station(id: string, systemId: string | null): string {
      const known = stations.get(id)
      if (known !== undefined) return known
      const parsed = parseStationId(id)
      const home = parsed === null ? null : placementSystemId(parsed.sysIndex)
      load(home)
      load(systemId)
      const name = stations.get(id)
      if (name !== undefined) return name
      const sysName = systems.get(home ?? '') ?? (systemId === null ? undefined : systems.get(systemId))
      return sysName === undefined ? 'Unknown station' : `${sysName} station`
    },
  }
}

/** Every station and relay of the Reach (the layouts are cached by the Reach; built once, on the first detail). */
let galaxySites: { stations: Choice[]; relays: Choice[] } | null = null

function sitesOf(ctx: GameContext): { stations: Choice[]; relays: Choice[] } {
  if (galaxySites !== null) return galaxySites
  const reach = ctx.reach
  const stations: Choice[] = []
  const relays: Choice[] = []
  if (reach !== null) {
    const sysName = new Map(reach.galaxy.systems.map((s) => [s.id, s.name]))
    for (const sys of reach.galaxy.systems) {
      const lay = reach.layoutOf(sys.id)
      for (const st of lay.stations) stations.push({ id: st.id, name: `${st.name} (${sys.name})` })
      for (const r of lay.relays) relays.push({ id: r.relayId, name: `${sys.name} → ${sysName.get(r.otherSystemId) ?? r.otherSystemId}` })
    }
  }
  galaxySites = { stations, relays }
  return galaxySites
}

const wareChoices = (ids: readonly string[]): Choice[] => ids.map((id) => ({ id, name: wareName(id) }))

/** The order editor's choices for `ship`: wares, systems, every station and relay, the player's stations, escorts. */
async function detailOptions(ctx: GameContext, ship: FleetShipView): Promise<EditorOptions> {
  const client = ctx.services.maybe('strategic')
  const fleet = ctx.services.maybe('fleet')
  const [own, view] = await Promise.all([client?.playerStationViews() ?? Promise.resolve([]), fleet?.fleetView() ?? Promise.resolve(null)])
  const sites = sitesOf(ctx)
  const playerStations: Choice[] = []
  for (const p of own) if (p.stationId !== null) playerStations.push({ id: p.stationId, name: p.name })
  const escorts: Choice[] = [{ id: 'player', name: 'You' }]
  for (const s of view?.ships ?? []) if (s.id !== ship.id) escorts.push({ id: s.id, name: s.name })
  return {
    shipId: ship.id,
    shipClass: ship.shipClass,
    wares: wareChoices(WARE_IDS),
    ores: wareChoices(RAW_WARE_IDS),
    systems: (ctx.reach?.galaxy.systems ?? []).map((s) => ({ id: s.id, name: s.name })),
    stations: sites.stations,
    playerStations,
    relays: sites.relays,
    escorts,
  }
}

/** A fitted module's name, base or pattern (`resolveModule`, MC-f2b); undefined for an id that is not a module. */
export function fittedModuleName(data: Pick<M2Data, 'modules'>, id: string): string | undefined {
  return isModuleId(id) ? resolveModule(data, id).name : undefined
}

/** The ship's fit: the flown ship's from the Career, else its hangar entry. */
function detailLoadout(ctx: GameContext, shipId: string): LoadoutLine[] | null {
  const m2 = ctx.services.maybe('m2')
  if (m2 === undefined) return null
  try {
    const career = m2.career()
    const fitted = career.flownShip === shipId ? career.ship.fitted : ownedShip(career, shipId)?.fitted
    if (fitted === undefined) return null
    return loadoutLines(fitted, (id) => fittedModuleName(m2.data, id))
  } catch {
    return null
  }
}

export function createFleetScreenFeature(): FleetScreenFeature {
  let overlay: FleetSectionHost | null = null
  let detail: ShipDetailOverlay | null = null
  let nav: PadNavState | null = null
  let paused = false
  let opener: ((shipId: string) => void) | null = null
  const offs: (() => void)[] = []

  const feature: FleetScreenFeature = {
    name: 'fleetScreen',
    order: ORDER,
    get screen(): FleetSectionHost | null {
      return overlay
    },

    init(ctx: GameContext): void {
      const service = ctx.services.maybe('fleet')
      if (service === undefined || ctx.reach === null) return
      const section = createFleetSection({
        service,
        names: reachNames(ctx.reach),
        config: loadFleetScreenConfig(),
        presets: loadPresets(),
        onOpenShip: (id) => opener?.(id),
        blocked: () => detail?.open === true,
      })
      overlay = section
      const client = ctx.services.maybe('strategic')
      if (client !== undefined) {
        const names = reachNames(ctx.reach)
        const d = createShipDetailOverlay({
          root: ctx.overlayRoot,
          ui: ctx.ui,
          service,
          client,
          data: loadOrderEditorData(),
          config: loadFleetScreenConfig(),
          options: (ship) => detailOptions(ctx, ship),
          loadout: (id) => detailLoadout(ctx, id),
          location: (ship) => shipLocation(ship, names),
          onSaved: () => overlay?.refresh(),
          onClosed: () => overlay?.redraw(),
          paused: () => paused,
        })
        detail = d
        opener = (id) => {
          d.show(id)
          overlay?.redraw()
        }
      }
      nav = createPadNav(loadSettingsConfig().menuNav, ctx.data.bindings.gamepad.buttons.pause)
      offs.push(registerDashSection(fleetSection('ships', section.component)))
    },

    openFleet(): void {
      openDashboard({ tab: 'fleet', section: FLEET_SECTION_IDS.ships })
    },

    setShipOpener(fn: ((shipId: string) => void) | null): void {
      opener = fn
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const o = overlay
      if (o === null || nav === null) return
      paused = f.paused
      const i = stepPadNav(nav, ctx.input.pad, f.dt)
      if (detail?.open === true) {
        if (i !== null) detail.input(i)
        return
      }
      if (o.shown && i !== null && i !== 'back' && i !== 'start' && !f.paused) o.input(i)
    },

    dispose(): void {
      for (const off of offs.splice(0)) off()
      detail?.dispose()
      detail = null
      overlay = null
      nav = null
      opener = null
    },
  }
  return feature
}

const feature: FleetScreenFeature = createFleetScreenFeature()

export default feature
